import moment from "moment";

// NOTE: Calculation logic has been moved to server-side centralized service
// All calculations now happen in /server/lib/tradeCalculations.js
// Trades use pre-calculated DB fields for performance

export const calculateDailyStats = (trades, date, showGross = true) => {
  if (!trades || trades.length === 0) {
    return {
      grossPnL: 0,
      percentReturn: 0,
      winRate: 0,
      tradeCount: 0,
      wins: 0,
      losses: 0,
      breakevens: 0,
    };
  }

  const dateStr = moment(date).format("DD/MM/YY");
  const tradesForDay = trades.filter((trade) => {
    return trade.entryDate === dateStr;
  });

  if (tradesForDay.length === 0) {
    return {
      grossPnL: 0,
      percentReturn: 0,
      winRate: 0,
      tradeCount: 0,
      wins: 0,
      losses: 0,
      breakevens: 0,
    };
  }

  let totalGrossPnL = 0;
  let totalNetPnL = 0;
  let wins = 0;
  let losses = 0;
  let breakevens = 0;

  // User is read once to resolve each trade's account balance for breakeven
  // fee/swap drag (gross % is 0), consistent with the Results Tracker net % formula.
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Get starting balances for percentage calculation
  const startingBalanceIC = parseFloat(user.startingBalanceIC) || 0;
  const startingBalancePC = parseFloat(user.startingBalancePC) || 0;

  tradesForDay.forEach((trade) => {
    const capital = trade.equityType;

    if (capital === "Investor Capital") {
      // Use DB-calculated outcome (all trades have this after migration)
      const outcomeIC = trade.outcomeIC;

      if (outcomeIC === "In Progress") return;

      const profitLossDollar = parseFloat(trade.profitLossDollarInvestor || 0);
      const netDollar = trade.netDollarIC || 0;
      totalGrossPnL += profitLossDollar;
      totalNetPnL += netDollar;

      if (outcomeIC === "Win") {
        wins++;
      } else if (outcomeIC === "Loss") {
        losses++;
      } else if (outcomeIC === "Breakeven") {
        breakevens++;
      }
    } else if (capital === "Personal Capital" || capital === "Backtest") {
      // Use DB-calculated outcome (all trades have this after migration)
      const outcomePC = trade.outcomePC;

      if (outcomePC === "In Progress") return;

      const profitLossDollar = parseFloat(trade.profitLossDollarPersonal || 0);
      const netDollar = trade.netDollarPC || 0;
      totalGrossPnL += profitLossDollar;
      totalNetPnL += netDollar;

      if (outcomePC === "Win") {
        wins++;
      } else if (outcomePC === "Loss") {
        losses++;
      } else if (outcomePC === "Breakeven") {
        breakevens++;
      }
    }
  });

  const totalTrades = wins + losses;
  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  
  // Calculate percentage from total dollars
  const totalPercentReturnGross = startingBalanceIC + startingBalancePC !== 0
    ? (totalGrossPnL / (startingBalanceIC + startingBalancePC)) * 100
    : 0;
  const totalPercentReturnNet = startingBalanceIC + startingBalancePC !== 0
    ? (totalNetPnL / (startingBalanceIC + startingBalancePC)) * 100
    : 0;

  const result = {
    grossPnL: showGross ? totalGrossPnL : totalNetPnL,
    percentReturn: showGross ? totalPercentReturnGross : totalPercentReturnNet,
    winRate: winRate,
    tradeCount: wins + losses + breakevens,
    wins: wins,
    losses: losses,
    breakevens: breakevens,
  };

  return result;
};

export const calculateWeeklyStats = (trades, weekStart, weekEnd, showGross = true) => {
  if (!trades || trades.length === 0) {
    return {
      grossPnL: 0,
      percentReturn: 0,
      winRate: 0,
      tradeCount: 0,
      daysTradedCount: 0,
      wins: 0,
      losses: 0,
    };
  }

  const tradesInWeek = trades.filter((trade) => {
    const tradeDate = moment(trade.entryDate, "DD/MM/YY");
    return tradeDate.isBetween(weekStart, weekEnd, null, "[]");
  });

  if (tradesInWeek.length === 0) {
    return {
      grossPnL: 0,
      percentReturn: 0,
      winRate: 0,
      tradeCount: 0,
      daysTradedCount: 0,
      wins: 0,
      losses: 0,
    };
  }

  let totalGrossPnL = 0;
  let totalNetPnL = 0;
  let wins = 0;
  let losses = 0;
  let breakevens = 0;
  const uniqueDays = new Set();

  // User is read once to resolve each trade's account balance for breakeven
  // fee/swap drag (gross % is 0), consistent with the Results Tracker net % formula.
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  tradesInWeek.forEach((trade) => {
    uniqueDays.add(trade.entryDate);
    const capital = trade.equityType;

    if (capital === "Investor Capital") {
      // Use DB-calculated outcome (all trades have this after migration)
      const outcomeIC = trade.outcomeIC;

      if (outcomeIC === "In Progress") return;

      const profitLossDollar = parseFloat(trade.profitLossDollarInvestor || 0);
      const netDollar = trade.netDollarIC || 0;
      totalGrossPnL += profitLossDollar;
      totalNetPnL += netDollar;

      if (outcomeIC === "Win") {
        wins++;
      } else if (outcomeIC === "Loss") {
        losses++;
      } else if (outcomeIC === "Breakeven") {
        breakevens++;
      }
    } else if (capital === "Personal Capital" || capital === "Backtest") {
      // Use DB-calculated outcome (all trades have this after migration)
      const outcomePC = trade.outcomePC;

      if (outcomePC === "In Progress") return;

      const profitLossDollar = parseFloat(trade.profitLossDollarPersonal || 0);
      const netDollar = trade.netDollarPC || 0;
      totalGrossPnL += profitLossDollar;
      totalNetPnL += netDollar;

      if (outcomePC === "Win") {
        wins++;
      } else if (outcomePC === "Loss") {
        losses++;
      } else if (outcomePC === "Breakeven") {
        breakevens++;
      }
    }
  });

  const totalTrades = wins + losses;
  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  
  // Get starting balances for percentage calculation
  const startingBalanceIC = parseFloat(user.startingBalanceIC) || 0;
  const startingBalancePC = parseFloat(user.startingBalancePC) || 0;
  
  // Calculate percentage from total dollars
  const totalPercentReturnGross = startingBalanceIC + startingBalancePC !== 0
    ? (totalGrossPnL / (startingBalanceIC + startingBalancePC)) * 100
    : 0;
  const totalPercentReturnNet = startingBalanceIC + startingBalancePC !== 0
    ? (totalNetPnL / (startingBalanceIC + startingBalancePC)) * 100
    : 0;

  const result = {
    grossPnL: showGross ? totalGrossPnL : totalNetPnL,
    percentReturn: showGross ? totalPercentReturnGross : totalPercentReturnNet,
    winRate: winRate,
    tradeCount: wins + losses + breakevens,
    daysTradedCount: uniqueDays.size,
    wins: wins,
    losses: losses,
  };

  return result;
};
